import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import StaticMap from 'components/StaticMap';
import MapSettings from 'components/Map/settings';
import { zoomToFeature, calculateArea } from 'components/StaticMap/helper';
import { removeAlertByGroup } from 'redux/alerts/actions';
import { useComponentMount } from 'utilities/hooks.util';
import Terraformer from 'terraformer-arcgis-parser';

export const MapView: React.FC<Props> = ({
  baseMapTheme,
  enquiryShape,
  onMapLoaded
}) => {
  const mapObjRef = useRef<any>({});
  const [mapReady, setMapReady] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onMapReady = () => {
    const { loadedView } = mapObjRef.current;
    if (loadedView) {
      // Wait for view to be fully ready in ArcGIS SDK 4.33+ before setting up event listeners
      loadedView
        .when(() => {
          loadedView.on('layerview-create', () => {
            if (onMapLoaded) {
              onMapLoaded();
            }
          });

          // Add a small delay to ensure all animations are initialized
          setTimeout(() => {
            setMapReady(true);
          }, 100);
        })
        .catch((error: any) => {
          console.error('Error initializing map view:', error);
          setMapReady(true);
        });
    }
  };

  const setEnquiryShapeToMap = useCallback(
    (shape: any) => {
      const { loadedMapModules, loadedMap } = mapObjRef.current;
      if (loadedMapModules) {
        const { Graphic, WebMercatorUtils, GraphicsLayer } = loadedMapModules;
        const currentFeatureLayer = new GraphicsLayer({
          id: 'current-referral-layer'
        });
        const geometry: any = Terraformer.convert(shape);
        const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
          geometry
        );
        const graphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          },
          symbol:
            baseMapTheme === 'dark'
              ? MapSettings.polygonDarkSymbol
              : MapSettings.polygonSymbol
        });
        currentFeatureLayer.add(graphic);
        loadedMap.add(currentFeatureLayer, 0);
        const area = calculateArea(mapObjRef.current, graphic.geometry.extent);
        const bufferSize = Math.sqrt(area) * 0.2;
        zoomToFeature(mapObjRef.current, graphic.geometry.extent, bufferSize);
        mapObjRef.current.mapOriginalExtent = graphic.geometry.extent;
      }
    },
    [baseMapTheme]
  );

  useEffect(() => {
    mapReady && enquiryShape && setEnquiryShapeToMap(enquiryShape);
  }, [enquiryShape, mapReady, setEnquiryShapeToMap]);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup('map-alert'));
      if (mapObjRef.current.loadedMap) {
        mapObjRef.current.loadedMap.removeAll();
        mapObjRef.current.loadedMap.destroy();
        mapObjRef.current.loadedMap = null;
      }
      if (mapObjRef.current.loadedView) {
        mapObjRef.current.loadedView.destroy();
        mapObjRef.current.loadedView = null;
      }
    };
  });

  return (
    <div className="map--view">
      <StaticMap onReady={onMapReady} mapObject={mapObjRef} />
    </div>
  );
};

type ownProps = {
  enquiryShape?: any;
  baseMapTheme?: String;
  onMapLoaded?: Function;
};

type Props = ownProps;

export default React.memo(MapView);
